import { Component, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { UntypedFormGroup, UntypedFormControl, Validators } from '@angular/forms';
import { Router } from '@angular/router';

import { NgbPopover } from '@ng-bootstrap/ng-bootstrap';
import { has } from 'lodash';

import { UserService } from '../user.service';
import { AUTH_PATHS } from '../../auth/auth';
import { MAIN_PATHS } from '../../main/main';
import { Enums } from '../../../configs/Enums';
import { BreadcrumbsService } from '../../../shared/breadcrumbs/breadcrumbs.service';
import { Breadcrumb } from '../../../shared/breadcrumbs/breadcrumbs';
import { isLegacyLoginEnabled } from '../../../../environments/legacy-login';

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'app-change-password-auth-user',
  templateUrl: './change-password-auth-user.component.html',
  styleUrls: ['./change-password-auth-user.component.scss'],
  standalone: false,
})
export class ChangePasswordAuthUserComponent implements OnInit {
  public AUTH_PATHS = AUTH_PATHS;
  public MAIN_PATHS = MAIN_PATHS;
  public changeAuthUserError: null | string;
  public beforePasswordError: null | boolean;
  public recoveryChangeError: null | boolean;
  public incorrectPasswordError: null | boolean;
  public authUserChangePasswordBreadcrumbs: Breadcrumb[] = [{ text: 'Изменение пароля', url: null }];
  public changePasswordForm: UntypedFormGroup = new UntypedFormGroup({
    password: new UntypedFormControl('', Validators.required),
    new_password: new UntypedFormControl('', [Validators.required, Validators.minLength(8)]),
    new_password_confirm: new UntypedFormControl({ value: '', disabled: true }, Validators.required),
    new_password_confirm_hidden: new UntypedFormControl(''),
  });

  public regxps = {
    range: /^[A-Za-z0-9]+$/,
    lowercase: /[a-z]/,
    uppercase: /[A-Z]/,
    numbers: /[\d]/,
  };

  public newPasswordValidation = {
    range: false,
    lowercase: false,
    uppercase: false,
    numbers: false,
    minLength: false,
    allMatch: false,
    passwordStatus: 'Ненадежный пароль',
  };

  @ViewChild('p') public popover: NgbPopover;

  constructor(
    private userService: UserService,
    private router: Router,
    private breadcrumbsService: BreadcrumbsService
  ) {}

  ngOnInit() {
    this.onChanges();

    this.breadcrumbsService.updateBreadcrumbs(this.authUserChangePasswordBreadcrumbs);
  }

  public openValidationPopover() {
    if (!this.newPasswordValidation.allMatch) {
      this.popover.open();
    }
  }

  public closeValidationPopover() {
    this.popover.close();
  }

  private checkRegexp(str: string, regexp: RegExp) {
    return regexp.test(str);
  }

  private onChanges() {
    this.changePasswordForm.get('new_password').valueChanges.subscribe((val) => {
      const validation = this.newPasswordValidation;
      const regxps = this.regxps;

      validation.range = this.checkRegexp(val, regxps.range);
      validation.lowercase = this.checkRegexp(val, regxps.lowercase);
      validation.uppercase = this.checkRegexp(val, regxps.uppercase);
      validation.numbers = this.checkRegexp(val, regxps.numbers);
      validation.minLength = val && val.length > 7;
      validation.allMatch = validation.range && validation.lowercase && validation.uppercase && validation.numbers && validation.minLength;

      if (validation.allMatch) {
        this.closeValidationPopover();
        this.changePasswordForm.controls['new_password_confirm'].enable();
      } else {
        this.openValidationPopover();
        this.changePasswordForm.controls['new_password_confirm'].disable();
      }

      if (this.changePasswordForm.value.new_password !== this.changePasswordForm.value.new_password_confirm) {
        this.changePasswordForm.controls['new_password_confirm_hidden'].setErrors({
          invalid: true,
        });
      } else {
        this.changePasswordForm.controls['new_password_confirm_hidden'].setErrors(null);
        this.changePasswordForm.controls['new_password_confirm'].setErrors(null);
      }

      this.resetErrors();
    });

    this.changePasswordForm.get('new_password_confirm').valueChanges.subscribe((val) => {
      if (val !== this.changePasswordForm.value.new_password) {
        this.changePasswordForm.controls['new_password_confirm'].setErrors({
          invalid: true,
        });
        this.changePasswordForm.controls['new_password_confirm_hidden'].setErrors({
          invalid: true,
        });
      } else {
        this.changePasswordForm.controls['new_password_confirm'].setErrors(null);
        this.changePasswordForm.controls['new_password_confirm_hidden'].setErrors(null);
      }

      this.resetErrors();
    });
  }

  private resetErrors() {
    this.changeAuthUserError = null;
    this.recoveryChangeError = null;
    this.beforePasswordError = null;
    this.incorrectPasswordError = null;
  }

  public onSubmitChangePasswordForm() {
    const changePasswordData = {
      password: this.changePasswordForm.value.password,
      new_password: this.changePasswordForm.value.new_password,
      new_password_confirm: this.changePasswordForm.value.new_password_confirm,
    };

    this.resetErrors();

    this.userService.changeCurrentPassword(changePasswordData).subscribe(
      (response) => {
        this.checkResponse(response['data'], response['metadata']);
      },
      (error) => {
        this.checkResponse(null, error.error['metadata']);
      }
    );
  }

  private checkResponse(data, metadata) {
    if (has(metadata, 'code')) {
      this.checkCode(metadata.code);
    } else {
      this.changeAuthUserError = Enums.errorCodes[Enums.errorCodesMaps['BACKEND_ERROR']];
    }
  }

  private checkCode(code) {
    switch (code) {
      case 0:
      case 100005:
        this.router.navigate(['/' + (isLegacyLoginEnabled() ? AUTH_PATHS.login_legacy : AUTH_PATHS.login)]);
        return;
      case 100007:
      case 100008:
      case 100009:
      case 100010:
        switch (code) {
          case 100007:
          case 100008:
            this.recoveryChangeError = true;
            break;
          case 100009:
            this.beforePasswordError = true;
            break;
          case 100010:
            this.incorrectPasswordError = true;
            break;
          default:
            break;
        }

        this.changeAuthUserError = Enums.errorCodes[code];
        return;
      default:
        this.changeAuthUserError = Enums.errorCodes[Enums.errorCodesMaps['BACKEND_ERROR']];
        return;
    }
  }
}
